import round from '../../../helpers/round';
import stressIncrease_A from '../../../assets/calculations/stressIncrease/stressIncrease_A_001.jpg';
import stressIncrease_B from '../../../assets/calculations/stressIncrease/stressIncrease_B_001.jpg';
import React, { useState } from 'react';
import { Alert, Typography, Box, IconButton, Collapse, Tooltip, useMediaQuery } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EastIcon from '@mui/icons-material/East';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Link from '@mui/material/Link';
import VerifiedIcon from '@mui/icons-material/Verified';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleSuccessIcon from '@mui/icons-material/CheckCircleOutline';

const StressIncreaseResult = ({ result }) => {
  const { q, B, L, z } = result.inputs;
  const { sigmaZ_60degree, sigmaZ_1H2V, canUserSeeCalculationSteps } = result.outputs;
  const isSmallScreen = useMediaQuery('(max-width:700px)');

  const StressIncrease60DegreeResult = () => {
    const [show60DegreeDetails, setShow60DegreeDetails] = useState(false);

    return (
      <Box sx={{ my: 2, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
            Stress Increase at Depth z using 30&deg; Approximation
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: isSmallScreen ? 'column' : 'row',
              gap: 2,
              alignItems: isSmallScreen ? 'stretch' : 'flex-start',
              mb: 2,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.6, textAlign: 'justify', flex: isSmallScreen ? '1 1 auto' : '3 1 0%' }}
            >
              The vertical stress increase (&sigma;<sub>z</sub>) at a depth (z) below the center of a rectangular loaded area can
              be estimated using the 30&deg; approximation. This method assumes that the applied surface load spreads uniformly
              into the soil mass along lines inclined at 30&deg; to the vertical, forming an idealized stress distribution with
              depth. <br />
              <br />
              It is a simplified approach commonly used for preliminary analyses, providing reasonable estimates for shallow
              depths in homogeneous soils. The method neglects the effects of soil layering, stress redistribution, and nonlinear
              stress concentration, and should therefore be used with caution for detailed design.
            </Typography>

            <Box
              component="img"
              src={stressIncrease_B}
              alt="30 degree stress distribution approximation"
              sx={{
                display: 'block',
                width: isSmallScreen ? '100%' : 'auto',
                flex: isSmallScreen ? '0 0 auto' : '1 1 0%',
                maxWidth: isSmallScreen ? '100%' : 320,
                borderRadius: 1,
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* 30 degree approxiamtion */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' },
                '& math': {
                  color: 'text.secondary',
                }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShow60DegreeDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: show60DegreeDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography variant="body2" color="text.secondary">
                <b>
                  &sigma;<sub>z</sub>
                </b>{' '}
                (stress increase at depth z={z ? z.toFixed(2) : '—'}m): {sigmaZ_60degree ? sigmaZ_60degree.toFixed(2) : '—'} kPa
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={show60DegreeDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    We can calculate the vertical stress increase (&sigma;<sub>z</sub>) at depth (z) using the formula:
                  </Typography>
                  <math display="block">
                    <msub>
                      <mi>&sigma;</mi>
                      <mi>z</mi>
                    </msub>
                    <mo>=</mo>
                    <mfrac>
                      <mrow>
                        <mi>q</mi>
                        <mo>&times;</mo>
                        <mi>B</mi>
                        <mo>&times;</mo>
                        <mi>L</mi>
                      </mrow>
                      <mrow>
                        <mo>(</mo>
                        <mi>L</mi>
                        <mo>+</mo>
                        <mn>2</mn>
                        <mo>&times;</mo>
                        <mi>z</mi>
                        <mo>&times;</mo>
                        <mi>tan</mi>
                        <mn>30</mn>
                        <mo>&deg;</mo>
                        <mo>)</mo>
                        <mo>(</mo>
                        <mi>B</mi>
                        <mo>+</mo>
                        <mn>2</mn>
                        <mo>&times;</mo>
                        <mi>z</mi>
                        <mo>&times;</mo>
                        <mi>tan</mi>
                        <mn>30</mn>
                        <mo>&deg;</mo>
                        <mo>)</mo>
                      </mrow>
                    </mfrac>
                  </math>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    Substituting the input values:
                  </Typography>
                  <math display="block">
                    <msub>
                      <mi>&sigma;</mi>
                      <mi>z</mi>
                    </msub>
                    <mtext> = </mtext>

                    <mfrac>
                      <mrow>
                        <mn>({q ? q.toFixed(2) : '—'})</mn>
                        <mo>&times;</mo>
                        <mn>({B ? B.toFixed(2) : '—'})</mn>
                        <mo>&times;</mo>
                        <mn>({L ? L.toFixed(2) : '—'})</mn>
                      </mrow>

                      <mrow>
                        <mo>(</mo>
                        <mn>{L.toFixed(2)}</mn>
                        <mo>+</mo>
                        <mn>2</mn>
                        <mo>&times;</mo>
                        <mn>{z ? z.toFixed(2) : '—'}</mn>
                        <mo>&times;</mo>
                        <mi>tan</mi>

                        <mn>30</mn>
                        <mo>&deg;</mo>
                        <mo>)</mo>

                        <mo>(</mo>
                        <mn>{B ? B.toFixed(2) : '—'}</mn>
                        <mo>+</mo>
                        <mn>2</mn>
                        <mo>&times;</mo>
                        <mn>{z ? z.toFixed(2) : '—'}</mn>
                        <mo>&times;</mo>
                        <mi>tan</mi>
                        <mn>30</mn>
                        <mo>&deg;</mo>
                        <mo>)</mo>
                      </mrow>
                    </mfrac>
                  </math>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    And finally, calculating the result gives:
                  </Typography>
                  <math display="block">
                    <msub>
                      <mi>&sigma;</mi>
                      <mi>z</mi>
                    </msub>
                    <mtext> = </mtext>

                    <mn>{sigmaZ_60degree ? sigmaZ_60degree.toFixed(2) : '—'} kPa</mn>
                  </math>
                </Collapse>
              )}
            </Alert>
          </Box>
        </Alert>
      </Box>
    );
  };
  const StressIncrease1H2VResult = () => {
    const [show1H2VDetails, setShow1H2VDetails] = useState(false);

    return (
      <Box sx={{ my: 2, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
            Stress Increase at Depth z using 1H:2V Approximation
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: isSmallScreen ? 'column' : 'row',
              gap: 2,
              alignItems: isSmallScreen ? 'stretch' : 'flex-start',
              mb: 2,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.6, textAlign: 'justify', flex: isSmallScreen ? '1 1 auto' : '3 1 0%' }}
            >
              The vertical stress increase (&sigma;<sub>z</sub>) at a depth (z) below the center of a rectangular loaded area can
              be estimated using the 1H:2V approximation. This method assumes that the applied surface load spreads uniformly into
              the soil mass with a horizontal-to-vertical distribution ratio of 1:2, resulting in a linearly expanding influence
              area with depth. <br />
              <br />
              It is a simplified approach commonly used for preliminary analyses, providing reasonable estimates for shallow
              depths in homogeneous soils. The method neglects the effects of soil layering, stress redistribution, and nonlinear
              stress concentration, and should therefore be used with caution for detailed design.
            </Typography>

            <Box
              component="img"
              src={stressIncrease_A}
              alt="30 degree stress distribution approximation"
              sx={{
                display: 'block',
                width: isSmallScreen ? '100%' : 'auto',
                flex: isSmallScreen ? '0 0 auto' : '1 1 0%',
                maxWidth: isSmallScreen ? '100%' : 320,
                borderRadius: 1,
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* 30 degree approxiamtion */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' },
                '& math': {
                  color: 'text.secondary',
                }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShow1H2VDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: show1H2VDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography variant="body2" color="text.secondary">
                <b>
                  &sigma;<sub>z</sub>
                </b>{' '}
                (stress increase at depth z={z ? z.toFixed(2) : '—'}m): {sigmaZ_1H2V ? sigmaZ_1H2V.toFixed(2) : '—'} kPa
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={show1H2VDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    We can calculate the vertical stress increase (&sigma;<sub>z</sub>) at depth (z) using the formula:
                  </Typography>
                  <math display="block">
                    <msub>
                      <mi>&sigma;</mi>
                      <mi>z</mi>
                    </msub>
                    <mo>=</mo>
                    <mfrac>
                      <mrow>
                        <mi>q</mi>
                        <mo>&times;</mo>
                        <mi>B</mi>
                        <mo>&times;</mo>
                        <mi>L</mi>
                      </mrow>
                      <mrow>
                        <mo>(</mo>
                        <mi>L</mi>
                        <mo>+</mo>
                        <mi>z</mi>
                        <mo>)</mo>
                        <mo>(</mo>
                        <mi>B</mi>
                        <mo>+</mo>
                        <mn>z</mn>

                        <mo>)</mo>
                      </mrow>
                    </mfrac>
                  </math>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    Substituting the input values:
                  </Typography>
                  <math display="block">
                    <msub>
                      <mi>&sigma;</mi>
                      <mi>z</mi>
                    </msub>
                    <mo>=</mo>
                    <mfrac>
                      <mrow>
                        <mn>({q ? q.toFixed(2) : '—'})</mn>
                        <mo>&times;</mo>
                        <mn>({B ? B.toFixed(2) : '—'})</mn>
                        <mo>&times;</mo>
                        <mn>({L ? L.toFixed(2) : '—'})</mn>
                      </mrow>
                      <mrow>
                        <mo>(</mo>
                        <mn>{L.toFixed(2)}</mn>
                        <mo>+</mo>
                        <mn>{z ? z.toFixed(2) : '—'}</mn>
                        <mo>)</mo>
                        <mo>(</mo>
                        <mn>{B ? B.toFixed(2) : '—'}</mn>
                        <mo>+</mo>
                        <mn>{z ? z.toFixed(2) : '—'}</mn>

                        <mo>)</mo>
                      </mrow>
                    </mfrac>
                  </math>

                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    And finally, calculating the result gives:
                  </Typography>
                  <math display="block">
                    <msub>
                      <mi>&sigma;</mi>
                      <mi>z</mi>
                    </msub>
                    <mtext> = </mtext>

                    <mn>{sigmaZ_1H2V ? sigmaZ_1H2V.toFixed(2) : '—'} kPa</mn>
                  </math>
                </Collapse>
              )}
            </Alert>
          </Box>
        </Alert>
      </Box>
    );
  };
  return (
    <section className="output stressIncreaseOutput">
      <h2>Output </h2>
      <StressIncrease60DegreeResult />
      <StressIncrease1H2VResult />
    </section>
  );
};

export default StressIncreaseResult;
